"use client";

import { useRef, useState } from "react";
import { platform, type DesignChange, type DesignPageInventory } from "./api";
import { Button } from "./ui";
import css from "./BuildyWorkspace.module.css";

export function WebsitePagePublishingSetup({
  websiteId,
  org,
  csrf,
  onPrepared,
}: {
  websiteId: string;
  org: number;
  csrf: string;
  onPrepared: () => void;
}) {
  const lock = useRef(false);
  const intent = useRef<{ version: number; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function prepare() {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const inventory = await platform<DesignPageInventory>(
        `websites/${websiteId}/pages/`,
        { org },
      );
      if (intent.current?.version !== inventory.source_version)
        intent.current = {
          version: inventory.source_version,
          key: crypto.randomUUID(),
        };
      await platform<DesignChange>(`websites/${websiteId}/prepare-pages/`, {
        method: "POST",
        org,
        csrf,
        body: {
          expected_source_version: intent.current.version,
          request_key: intent.current.key,
        },
      });
      onPrepared();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not prepare page publishing.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <section className={css.panel}>
      <div className={css.panelHeading}>
        <h2>Customer maintenance setup</h2>
      </div>
      <div className={css.panelBody}>
        <p>
          Prepare older websites for direct editing and page publishing. AI
          creates a draft for review using the normal website credit allowance.
          Save changes after reviewing it.
        </p>
        {error && <p role="alert">{error}</p>}
        <Button disabled={busy} onClick={() => void prepare()}>
          {busy ? "Preparing…" : "Prepare website"}
        </Button>
      </div>
    </section>
  );
}
